import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, money, CLIENT_TYPE_LABELS, ApiError } from '../api'
import { Page } from '../App'
import { Card, Badge, Alert, Spinner, Empty, Modal, Field } from '../components/ui'

export interface Client {
  id: string
  type: string
  legal_name: string
  display_name: string | null
  gstin: string | null
  pan: string | null
  email: string | null
  phone: string | null
  billing_line1: string | null
  billing_line2: string | null
  billing_city: string | null
  billing_state: string | null
  billing_state_code: string | null
  billing_pincode: string | null
  billing_country: string
  currency: string
  payment_terms_days: number
  default_sac: string | null
  default_tax_rate: number
  export_mode: string
  reminders_muted: number
  notes: string | null
  is_active: number
  outstanding?: number
  invoice_count?: number
}

export default function Clients() {
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [editing, setEditing] = useState<Client | null>(null)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  const load = async () => {
    setLoading(true)
    try {
      const res = await api.get<{ clients: Client[] }>(`/clients${q ? `?q=${encodeURIComponent(q)}` : ''}`)
      setClients(res.clients)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load clients')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q])

  return (
    <Page
      title="Clients"
      subtitle={`${clients.length} active`}
      actions={
        <button className="btn primary" onClick={() => setCreating(true)}>
          + New client
        </button>
      }
    >
      <Alert kind="error">{error}</Alert>

      <div style={{ marginBottom: 14, maxWidth: 340 }}>
        <input
          type="text"
          placeholder="Search name, GSTIN or email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <Card tight>
        {loading ? (
          <Spinner />
        ) : clients.length === 0 ? (
          <Empty icon="◍" title="No clients yet" hint="Add a client to start invoicing." />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Type</th>
                  <th>GSTIN</th>
                  <th>Terms</th>
                  <th className="r">Outstanding</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr key={c.id}>
                    <td className="clickable" onClick={() => navigate(`/clients/${c.id}`)}>
                      <div style={{ fontWeight: 600 }}>{c.display_name || c.legal_name}</div>
                      <div className="muted small">
                        {[c.billing_city, c.billing_state || c.billing_country].filter(Boolean).join(', ') || c.email}
                      </div>
                    </td>
                    <td className="small">{CLIENT_TYPE_LABELS[c.type] ?? c.type}</td>
                    <td className="mono small">{c.gstin ?? <span className="muted">—</span>}</td>
                    <td className="small nowrap">Net {c.payment_terms_days}</td>
                    <td className="num">
                      {c.outstanding ? (
                        <strong>{money(c.outstanding, c.currency)}</strong>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="right nowrap">
                      {c.reminders_muted ? <Badge status="draft">Muted</Badge> : null}{' '}
                      <button className="btn sm" onClick={() => setEditing(c)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {(creating || editing) && (
        <ClientForm
          client={editing}
          onClose={() => {
            setCreating(false)
            setEditing(null)
          }}
          onSaved={() => {
            setCreating(false)
            setEditing(null)
            load()
          }}
        />
      )}
    </Page>
  )
}

export function ClientForm({
  client,
  onClose,
  onSaved,
}: {
  client: Client | null
  onClose: () => void
  onSaved: () => void
}) {
  const [form, setForm] = useState<Record<string, unknown>>(
    (client as unknown as Record<string, unknown>) ?? {
      type: 'domestic_b2b',
      billing_country: 'India',
      currency: 'INR',
      payment_terms_days: 15,
      default_tax_rate: 18,
      export_mode: 'lut',
      shipping_same: 1,
    },
  )
  const [states, setStates] = useState<{ code: string; name: string }[]>([])
  const [error, setError] = useState('')
  const [field, setField] = useState<string | undefined>()
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api.get<{ states: { code: string; name: string }[] }>('/clients/state-codes').then((r) => setStates(r.states))
  }, [])

  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }))
  const isExport = form.type === 'export'

  const save = async () => {
    setError('')
    setField(undefined)
    setBusy(true)
    try {
      if (client) await api.patch(`/clients/${client.id}`, form)
      else await api.post('/clients', form)
      onSaved()
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message)
        setField(e.field)
      } else setError('Save failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      wide
      title={client ? `Edit ${client.legal_name}` : 'New client'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save client'}
          </button>
        </>
      }
    >
      <Alert kind="error">{error}</Alert>

      <div className="grid c2">
        <Field label="Client type" hint="Drives the whole tax calculation — pick carefully.">
          <select value={String(form.type)} onChange={(e) => set('type', e.target.value)}>
            {Object.entries(CLIENT_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Legal name" error={field === 'legal_name' ? error : undefined}>
          <input type="text" value={String(form.legal_name ?? '')} onChange={(e) => set('legal_name', e.target.value)} />
        </Field>
        <Field label="Display name" hint="Optional short name used in lists and emails.">
          <input type="text" value={String(form.display_name ?? '')} onChange={(e) => set('display_name', e.target.value)} />
        </Field>
        <Field
          label="GSTIN"
          error={field === 'gstin' ? error : undefined}
          hint="Checksum-validated. The state is taken from here."
        >
          <input
            type="text"
            className="mono"
            maxLength={15}
            value={String(form.gstin ?? '')}
            onChange={(e) => set('gstin', e.target.value.toUpperCase())}
            placeholder={isExport ? 'Not applicable for exports' : '27AABCU9603R1ZM'}
            disabled={isExport}
          />
        </Field>
        <Field label="Email" hint="Invoices and reminders go here unless contacts are set.">
          <input type="email" value={String(form.email ?? '')} onChange={(e) => set('email', e.target.value)} />
        </Field>
        <Field label="Phone">
          <input type="text" value={String(form.phone ?? '')} onChange={(e) => set('phone', e.target.value)} />
        </Field>
      </div>

      <div className="sep" />
      <h3 style={{ marginBottom: 10 }}>Billing address</h3>
      <Field label="Address line 1">
        <input type="text" value={String(form.billing_line1 ?? '')} onChange={(e) => set('billing_line1', e.target.value)} />
      </Field>
      <Field label="Address line 2">
        <input type="text" value={String(form.billing_line2 ?? '')} onChange={(e) => set('billing_line2', e.target.value)} />
      </Field>
      <div className="grid c3">
        <Field label="City">
          <input type="text" value={String(form.billing_city ?? '')} onChange={(e) => set('billing_city', e.target.value)} />
        </Field>
        <Field label="State" hint={isExport ? 'Not applicable' : 'Sets place of supply'}>
          <select
            value={String(form.billing_state_code ?? '')}
            onChange={(e) => set('billing_state_code', e.target.value)}
            disabled={isExport}
          >
            <option value="">Select…</option>
            {states.map((s) => (
              <option key={s.code} value={s.code}>
                {s.code} — {s.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="PIN code">
          <input type="text" value={String(form.billing_pincode ?? '')} onChange={(e) => set('billing_pincode', e.target.value)} />
        </Field>
      </div>
      <Field label="Country" error={field === 'billing_country' ? error : undefined}>
        <input type="text" value={String(form.billing_country ?? 'India')} onChange={(e) => set('billing_country', e.target.value)} />
      </Field>

      <div className="sep" />
      <h3 style={{ marginBottom: 10 }}>Billing defaults</h3>
      <div className="grid c3">
        <Field label="Currency">
          <select value={String(form.currency ?? 'INR')} onChange={(e) => set('currency', e.target.value)}>
            {['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD', 'AUD', 'CAD'].map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Payment terms (days)">
          <input
            type="number"
            value={Number(form.payment_terms_days ?? 15)}
            onChange={(e) => set('payment_terms_days', Number(e.target.value))}
          />
        </Field>
        <Field label="Default GST rate (%)">
          <input
            type="number"
            value={Number(form.default_tax_rate ?? 18)}
            onChange={(e) => set('default_tax_rate', Number(e.target.value))}
          />
        </Field>
      </div>

      {(isExport || form.type === 'sez') && (
        <Field
          label="Zero-rating mode"
          hint="Under LUT means no IGST on the invoice. With IGST means you pay and claim a refund."
        >
          <select value={String(form.export_mode ?? 'lut')} onChange={(e) => set('export_mode', e.target.value)}>
            <option value="lut">Under LUT, without payment of IGST</option>
            <option value="with_igst">With payment of IGST (refund claimed)</option>
          </select>
        </Field>
      )}

      <Field label="Default SAC/HSN">
        <input type="text" className="mono" value={String(form.default_sac ?? '')} onChange={(e) => set('default_sac', e.target.value)} />
      </Field>

      <label className="check" style={{ marginBottom: 12 }}>
        <input
          type="checkbox"
          checked={Boolean(form.reminders_muted)}
          onChange={(e) => set('reminders_muted', e.target.checked ? 1 : 0)}
        />
        Mute automatic payment reminders for this client
      </label>

      <Field label="Internal notes">
        <textarea value={String(form.notes ?? '')} onChange={(e) => set('notes', e.target.value)} />
      </Field>
    </Modal>
  )
}
